import { request } from '@/utils/request';

/**
 * 用户名密码登录
 * @param {string} username 用户名
 * @param {string} password 密码
 * @param {boolean} remember 是否记住登录状态
 * @returns {Promise} 登录结果
 */
export async function login(username, password, remember = false) {
  return request.post('/api/v1/users/login', { username, password, remember });
}

/**
 * 发送验证码
 * @param {string} type 验证码类型: "phone" 或 "email"
 * @param {string} target 手机号或邮箱地址
 * @param {string} purpose 用途: "login", "register", "reset_password"
 * @returns {Promise} 发送结果
 */
export async function sendVerificationCode(type, target, purpose = 'login') {
  return request.post('/api/v1/users/send-verification-code', { type, target, purpose });
}

/**
 * 手机号验证码登录
 * @param {string} phone 手机号
 * @param {string} verificationCode 验证码
 * @param {boolean} remember 是否记住登录状态
 * @returns {Promise} 登录结果
 */
export async function loginWithPhone(phone, verificationCode, remember = false) {
  return request.post('/api/v1/users/login-phone', { 
    phone, 
    verification_code: verificationCode, 
    remember 
  });
}

/**
 * 邮箱验证码登录
 * @param {string} email 邮箱地址
 * @param {string} verificationCode 验证码
 * @param {boolean} remember 是否记住登录状态
 * @returns {Promise} 登录结果
 */
export async function loginWithEmail(email, verificationCode, remember = false) {
  return request.post('/api/v1/users/login-email', { 
    email, 
    verification_code: verificationCode, 
    remember 
  });
}

// 微信相关功能已迁移到 @/services/wechatService.js

/**
 * 获取当前用户信息
 * @returns {Promise} 用户信息
 */
export async function getCurrentUser() {
  return request.get('/api/v1/users/current');
}

/**
 * 用户登出
 * @returns {Promise} 登出结果
 */
export async function logout() {
  return request.post('/api/v1/users/logout');
}

/**
 * 刷新Token
 * @param {string} refreshToken 刷新令牌
 * @returns {Promise} 新的Token
 */
export async function refreshToken(refreshToken) {
  return request.post('/api/v1/users/refresh', { refreshToken });
}

/**
 * 获取账户交易记录
 * @param {Object} params 查询参数
 * @param {number} params.page 页码，默认1
 * @param {number} params.page_size 每页数量，默认10，最大100
 * @param {string} params.transaction_type 交易类型（可选）
 * @param {string} params.service_type 服务类型（可选）
 * @param {string} params.record_type 记录类型："balance" | "quota" | "all"（可选）
 * @param {string} params.start_date 开始日期（可选）
 * @param {string} params.end_date 结束日期（可选）
 * @returns {Promise} 交易记录列表
 */
export async function getTransactionHistory(params = {}) {
  const { 
    page = 1, 
    page_size = 10, 
    transaction_type, 
    service_type, 
    record_type = 'all',
    start_date, 
    end_date 
  } = params;
  
  const queryParams = new URLSearchParams({
    page: page.toString(),
    page_size: page_size.toString()
  });
  
  if (transaction_type) {
    queryParams.append('transaction_type', transaction_type);
  }
  
  if (service_type) {
    queryParams.append('service_type', service_type);
  }
  
  if (record_type && record_type !== 'all') {
    queryParams.append('record_type', record_type);
  }
  
  if (start_date) {
    queryParams.append('start_date', start_date);
  }
  
  if (end_date) {
    queryParams.append('end_date', end_date);
  }
  
  return request.get(`/api/v1/account/transactions?${queryParams.toString()}`);
}

/**
 * 获取账户余额信息
 * @returns {Promise} 账户余额信息
 */
export async function getAccountBalance() {
  return request.get('/api/v1/account/info');
} 